import { ApolloError, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { isNullish } from 'remeda';
import {
  CHANGE_WORKFLOW_VERSION_ACTIVE_STATE_MUTATION,
  CREATE_DRAFT_WORKFLOW_VERSION_MUTATION,
  SOFT_DELETE_WORKFLOW_VERSION_MUTATION,
} from 'src/graphql/entities/workflow/workflow.mutations';
import {
  ChangeWorkflowVersionActiveStateMutation,
  CreateDraftWorkflowVersionMutation,
  SoftDeleteWorkflowVersionMutation,
} from 'src/graphql/entities/workflow/workflow.types';
import { useNotifications } from 'src/utils/hooks';

enum WorkflowLifecycleErrorCode {
  Unauthorized = 'Unauthorized',
  UnauthorizedException = 'UNAUTHORIZED',
}

const UNAUTHORIZED_WORKFLOW_LIFECYCLE_ERROR_MESSAGE =
  'You do not have permission to manage workflow versions.';

/**
 * Every mounted query that reads workflow versions, by operation name.
 *
 * A version action changes `isActive` on two rows at once, and those rows are normalised —
 * shared by the page's own list, the assignments prefetch, and the emergency override card.
 * Refetching only the instance that triggered the mutation leaves the others holding a
 * pre-mutation view, and whichever answers last writes it back over the cache: the header
 * flips to Active and then quietly flips back. Naming the operations refetches every live
 * instance whatever its variables.
 */
export const WORKFLOW_VERSION_QUERIES = ['GetWorkflowVersions', 'GetWorkflows'];

const WORKFLOW_LIFECYCLE_ERROR_MESSAGES = {
  createDraft: 'Failed to create draft workflow version. Please try again.',
  updateState: 'Failed to update workflow version state. Please try again.',
  deleteVersion: 'Failed to delete workflow version. Please try again.',
} as const;

const getGraphQLErrorMachineCode = (
  graphQLError: ApolloError['graphQLErrors'][number],
): string | null => {
  const errorType =
    typeof graphQLError.extensions?.errorType === 'string'
      ? graphQLError.extensions.errorType
      : null;
  const errorCode =
    typeof graphQLError.extensions?.code === 'string' ? graphQLError.extensions.code : null;

  return errorType ?? errorCode;
};

export const getLifecycleErrorMessage = (
  error: ApolloError,
  fallbackMessage: string,
): string => {
  for (const graphQLError of error.graphQLErrors) {
    const machineCode = getGraphQLErrorMachineCode(graphQLError);

    if (
      machineCode === WorkflowLifecycleErrorCode.Unauthorized ||
      machineCode === WorkflowLifecycleErrorCode.UnauthorizedException
    ) {
      return UNAUTHORIZED_WORKFLOW_LIFECYCLE_ERROR_MESSAGE;
    }

    if (graphQLError.message) {
      return graphQLError.message;
    }
  }

  if (error.networkError) {
    return fallbackMessage;
  }

  if (error.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const useWorkflowVersionMutations = ({
  partnerId,
  refetchWorkflowVersions,
}: {
  partnerId?: number | null;
  refetchWorkflowVersions?: () => Promise<unknown>;
}) => {
  const { showErrorAlert } = useNotifications();

  const [createDraftWorkflowVersionMutation, { loading: isCreatingDraft }] =
    useMutation<CreateDraftWorkflowVersionMutation>(CREATE_DRAFT_WORKFLOW_VERSION_MUTATION, {
      refetchQueries: WORKFLOW_VERSION_QUERIES,
      awaitRefetchQueries: true,
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          getLifecycleErrorMessage(error, WORKFLOW_LIFECYCLE_ERROR_MESSAGES.createDraft),
        );
      },
    });

  const [changeWorkflowVersionActiveStateMutation, { loading: isUpdatingActiveState }] =
    useMutation<ChangeWorkflowVersionActiveStateMutation>(
      CHANGE_WORKFLOW_VERSION_ACTIVE_STATE_MUTATION,
      {
        refetchQueries: WORKFLOW_VERSION_QUERIES,
        awaitRefetchQueries: true,
        onError: (error) => {
          console.error(error);
          showErrorAlert(
            getLifecycleErrorMessage(error, WORKFLOW_LIFECYCLE_ERROR_MESSAGES.updateState),
          );
        },
      },
    );

  const [softDeleteWorkflowVersionMutation, { loading: isDeletingVersion }] =
    useMutation<SoftDeleteWorkflowVersionMutation>(SOFT_DELETE_WORKFLOW_VERSION_MUTATION, {
      refetchQueries: WORKFLOW_VERSION_QUERIES,
      awaitRefetchQueries: true,
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          getLifecycleErrorMessage(error, WORKFLOW_LIFECYCLE_ERROR_MESSAGES.deleteVersion),
        );
      },
    });

  const ensurePartnerId = useCallback((): number | null => {
    if (isNullish(partnerId)) {
      showErrorAlert('Partner not selected.');
      return null;
    }

    return partnerId;
  }, [partnerId, showErrorAlert]);

  const refetchVersions = useCallback(async () => {
    if (!refetchWorkflowVersions) {
      return;
    }

    await refetchWorkflowVersions();
  }, [refetchWorkflowVersions]);

  const createDraftWorkflowVersion = useCallback(
    async ({
      workflowId,
      definition,
      description,
    }: {
      workflowId: string;
      definition: string;
      description?: string;
    }) => {
      const resolvedPartnerId = ensurePartnerId();
      if (isNullish(resolvedPartnerId)) {
        return null;
      }

      const { data } = await createDraftWorkflowVersionMutation({
        variables: {
          input: {
            workflowId,
            partnerId: resolvedPartnerId,
            definition,
            description,
          },
        },
      });

      await refetchVersions();

      return data?.createDraftWorkflowVersion ?? null;
    },
    [createDraftWorkflowVersionMutation, ensurePartnerId, refetchVersions],
  );

  const changeWorkflowVersionActiveState = useCallback(
    async ({
      workflowVersionId,
      active,
      siblingVersionIds = [],
    }: {
      workflowVersionId: string;
      active: boolean;
      /**
       * The other versions of the same workflow. Only one version may be active, so
       * activating this one clears them — passed in because the cache cannot enumerate an
       * entity's siblings, and waiting for the refetch would leave two dots lit meanwhile.
       */
      siblingVersionIds?: string[];
    }) => {
      const resolvedPartnerId = ensurePartnerId();
      if (isNullish(resolvedPartnerId)) {
        return null;
      }

      const { data } = await changeWorkflowVersionActiveStateMutation({
        variables: {
          input: {
            workflowVersionId,
            partnerId: resolvedPartnerId,
            active,
          },
        },
        // The refetch below is a round trip, so on its own the dot only moves once the
        // network answers. Writing the new state straight into the normalised cache moves it
        // on the spot; the refetch then confirms it.
        update: (cache) => {
          const setActive = (id: string, isActive: boolean) =>
            cache.modify({
              id: cache.identify({ __typename: 'WorkflowVersion', id }),
              fields: { isActive: () => isActive },
            });

          setActive(workflowVersionId, active);

          if (active) siblingVersionIds.forEach((id) => setActive(id, false));
        },
      });

      await refetchVersions();

      return data?.changeWorkflowVersionActiveState ?? null;
    },
    [changeWorkflowVersionActiveStateMutation, ensurePartnerId, refetchVersions],
  );

  const softDeleteWorkflowVersion = useCallback(
    async (workflowVersionId: string) => {
      const resolvedPartnerId = ensurePartnerId();
      if (isNullish(resolvedPartnerId)) {
        return false;
      }

      const { data } = await softDeleteWorkflowVersionMutation({
        variables: {
          input: {
            workflowVersionId,
            partnerId: resolvedPartnerId,
          },
        },
      });

      await refetchVersions();

      return data?.softDeleteWorkflowVersion ?? false;
    },
    [ensurePartnerId, refetchVersions, softDeleteWorkflowVersionMutation],
  );

  return {
    createDraftWorkflowVersion,
    changeWorkflowVersionActiveState,
    softDeleteWorkflowVersion,
    loading: {
      isCreatingDraft,
      isUpdatingActiveState,
      isDeletingVersion,
      isMutating: isCreatingDraft || isUpdatingActiveState || isDeletingVersion,
    },
  };
};
